import React from 'react';
import { Scale, X, Tag } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { TabularNumber } from '@/components/common/TabularNumber';
import { PigSaleRecord } from '@/types/database';

interface ScaleItemsDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  sale: PigSaleRecord | null;
  onPrintSlip?: () => void;
}

export const ScaleItemsDetailModal: React.FC<ScaleItemsDetailModalProps> = ({
  isOpen,
  onClose,
  sale,
  onPrintSlip,
}) => {
  if (!sale) return null;

  const items = sale.pig_sale_items || [];
  const headCount = Number(sale.head_count) || items.length;
  const grossKg = Number(sale.gross_weight_kg) || 0;
  const shrinkKg = Number(sale.shrinkage_deduction_kg) || 0;
  const netKg = Number(sale.net_weight_kg) || Math.max(0, grossKg - shrinkKg);
  const totalAmount = Number(sale.total_amount) || 0;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-4 sm:p-6 overflow-hidden">
        <DialogHeader className="flex flex-row items-center justify-between pb-3 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center">
              <Scale className="w-4 h-4" />
            </div>
            <div>
              <DialogTitle className="text-base font-bold text-slate-900">
                Scale Weights Breakdown — Ticket #{sale.scale_ticket_no || sale.sale_number || 'N/A'}
              </DialogTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                Cycle: <span className="font-mono font-bold text-slate-800">{sale.production_cycles?.cycle_code || 'N/A'}</span> • Buyer:{' '}
                <span className="font-semibold text-slate-800">{sale.buyers?.buyer_name || 'N/A'}</span> • Date: {sale.sale_date}
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Summary Metric Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs">
          <div>
            <span className="text-[10px] text-slate-500 uppercase tracking-wider block font-bold">Pigs Sold</span>
            <span className="text-sm font-bold font-mono text-emerald-700">{headCount} head</span>
          </div>
          <div>
            <span className="text-[10px] text-slate-500 uppercase tracking-wider block font-bold">Gross Scale Wt</span>
            <span className="text-sm font-bold font-mono text-slate-900">
              <TabularNumber value={grossKg} decimals={2} /> kg
            </span>
          </div>
          <div>
            <span className="text-[10px] text-slate-500 uppercase tracking-wider block font-bold">Shrinkage Deduction</span>
            <span className="text-sm font-bold font-mono text-amber-700">
              -<TabularNumber value={shrinkKg} decimals={2} /> kg ({sale.shrinkage_rate_applied} kg/hd)
            </span>
          </div>
          <div>
            <span className="text-[10px] text-slate-500 uppercase tracking-wider block font-bold">Net Sale Revenue</span>
            <span className="text-sm font-bold font-mono text-emerald-700">
              <TabularNumber value={totalAmount} type="currency" decimals={2} />
            </span>
          </div>
        </div>

        {/* Scale Items Table */}
        <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg bg-white no-scrollbar">
          {items.length > 0 ? (
            <table className="w-full text-xs text-left border-collapse">
              <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200 sticky top-0">
                <tr>
                  <th className="py-2 px-2.5 w-10 text-center">#</th>
                  <th className="py-2 px-2 w-14 text-center">Qty</th>
                  <th className="py-2 px-2.5 text-right">Gross Wt (kg)</th>
                  <th className="py-2 px-2 text-right text-amber-800 bg-amber-50/50">Shrink (kg)</th>
                  <th className="py-2 px-2.5 text-right text-emerald-800 bg-emerald-50/50">Net Wt (kg)</th>
                  <th className="py-2 px-2.5 text-right">Base Price</th>
                  <th className="py-2 px-2.5 text-right">Line Total</th>
                  <th className="py-2 px-2.5">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-mono">
                {items.map((it) => {
                  const qty = Number(it.head_count) || 1;
                  const gross = Number(it.gross_weight_kg) || 0;
                  const shrink = Number(it.shrinkage_kg || (Number(sale.shrinkage_rate_applied) || 0) * qty);
                  const net = Number(it.net_weight_kg || Math.max(0, gross - shrink));
                  const price = Number(it.price_per_kg || sale.price_per_kg || 0);
                  const lineTotal = Number(it.total_amount || (net * price));

                  return (
                    <tr key={it.id || it.sequence_no} className="hover:bg-slate-50">
                      <td className="py-1.5 px-2.5 text-center text-slate-500">{it.sequence_no}</td>
                      <td className="py-1.5 px-2 text-center text-slate-800 font-bold">{qty}</td>
                      <td className="py-1.5 px-2.5 text-right font-medium text-slate-900">
                        <TabularNumber value={gross} decimals={2} />
                      </td>
                      <td className="py-1.5 px-2 text-right text-amber-800 bg-amber-50/20">
                        <TabularNumber value={shrink} decimals={2} />
                      </td>
                      <td className="py-1.5 px-2.5 text-right font-bold text-emerald-800 bg-emerald-50/20">
                        <TabularNumber value={net} decimals={2} />
                      </td>
                      <td className="py-1.5 px-2.5 text-right text-slate-700">
                        ₱{price.toFixed(2)}
                      </td>
                      <td className="py-1.5 px-2.5 text-right font-bold text-slate-900">
                        <TabularNumber value={lineTotal} type="currency" decimals={2} />
                      </td>
                      <td className="py-1.5 px-2.5 text-slate-500 font-sans text-[11px]">{it.notes || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="p-8 text-center text-slate-500 text-xs font-mono">
              Grouped historical record: {headCount} head @ {grossKg.toFixed(2)} kg total
            </div>
          )}
        </div>

        <DialogFooter className="pt-3 border-t border-slate-200 gap-2">
          {onPrintSlip && (
            <Button
              type="button"
              size="sm"
              onClick={onPrintSlip}
              className="bg-emerald-700 hover:bg-emerald-600 text-white font-medium"
            >
              Print Scale Slip
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
